import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Stage, Layer } from 'react-konva';
import { CanvasToolbar, StickyNote, Shape, TextElement, DrawingLine, Transformer, Connector } from '../components/canvas';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User, Cloud, CloudOff, Save, Edit2, Check, X, FolderOpen, FilePlus, Trash2, HardDrive, FileDown, FileUp, Menu, ToggleLeft, ToggleRight } from 'lucide-react';
import { saveCanvasToCloud, loadCanvasFromCloud, isCloudSaveAvailable, listSavedCanvases, deleteCanvasFromCloud } from '../services/canvasDataService';
import accountService from '../services/accountService';
import toast, { Toaster } from 'react-hot-toast';
import { findMindmapRoot, calculateConnectorAnchors, findOptimalChildPosition, getChildNodes } from '../utils/mindmapLayout';

const GRID_SIZE = 50;
const STICKY_COLORS = ['#fef08a', '#fecaca', '#bfdbfe', '#d9f99d', '#fde68a', '#e9d5ff'];

export const CanvasPage = () => {
  const { user, logout } = useAuth();
  const [activeTool, setActiveTool] = useState('select');
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [historyStep, setHistoryStep] = useState(0);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentLine, setCurrentLine] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [connectorStart, setConnectorStart] = useState(null);
  const [connectorPreview, setConnectorPreview] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#93c5fd');
  const [selectedStroke, setSelectedStroke] = useState('#3b82f6');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [textFontSize, setTextFontSize] = useState(16);
  const [isTextBold, setIsTextBold] = useState(false);
  const [isTextItalic, setIsTextItalic] = useState(false);
  const [isTextStrikethrough, setIsTextStrikethrough] = useState(false);
  const [cloudSaveStatus, setCloudSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastCloudSave, setLastCloudSave] = useState(null);
  const [canvasName, setCanvasName] = useState('Untitled Canvas');
  const [canvasId, setCanvasId] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempCanvasName, setTempCanvasName] = useState('');
  const [showLoadMenu, setShowLoadMenu] = useState(false);
  const [savedCanvases, setSavedCanvases] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasAutoCentered, setHasAutoCentered] = useState(false);
  const [stageSize, setStageSize] = useState({ width: window.innerWidth, height: window.innerHeight - 110 });
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [cloudAutoSaveEnabled, setCloudAutoSaveEnabled] = useState(true);
  
  const stageRef = useRef();
  const containerRef = useRef();
  const loadMenuRef = useRef();
  const pinchLastDistRef = useRef(null);
  const pinchLastCenterRef = useRef(null);

  // Load canvas from cloud or localStorage on mount
  useEffect(() => {
    const loadCanvas = async () => {
      if (!user) return;

      // Try to load most recent canvas from cloud first
      const token = accountService.getAccessToken();
      if (token && isCloudSaveAvailable(token)) {
        try {
          const cloudData = await loadCanvasFromCloud(token);
          if (cloudData && cloudData.elements) {
            console.log('Loaded most recent canvas from cloud');
            setElements(cloudData.elements);
            setHistory([cloudData.elements]);
            setLastCloudSave(new Date(cloudData.timestamp));
            if (cloudData.name) setCanvasName(cloudData.name);
            if (cloudData.id) setCanvasId(cloudData.id);
            if (cloudData.scale) setScale(cloudData.scale);
            if (cloudData.stagePos) setStagePos(cloudData.stagePos);
            setHasUnsavedChanges(false);
            setHasAutoCentered(false);
            return;
          }
        } catch (error) {
          console.warn('Could not load from cloud, trying localStorage:', error);
        }
      }

      // Fallback to localStorage
      const savedCanvas = localStorage.getItem(`canvas_${user?.email || 'default'}`);
      if (savedCanvas) {
        try {
          const data = JSON.parse(savedCanvas);
          console.log('Loaded canvas from localStorage');
          setElements(data.elements || []);
          setHistory([data.elements || []]);
          if (data.scale) setScale(data.scale);
          if (data.stagePos) setStagePos(data.stagePos);
          setHasAutoCentered(false);
        } catch (error) {
          console.error('Error loading canvas:', error);
        }
      }
    };

    loadCanvas();
  }, [user]);

  // Keep stage size in sync with container size (handles mobile keyboard/orientation changes)
  useEffect(() => {
    const updateStageSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setStageSize({ width: rect.width, height: rect.height });
      } else {
        setStageSize({ width: window.innerWidth, height: window.innerHeight - 110 });
      }
    };

    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
  }, []);

  // Save canvas to localStorage whenever elements change (debounced for scale/stagePos)
  const saveTimeoutRef = useRef(null);
  useEffect(() => {
    if (elements.length > 0 || history.length > 1) {
      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Debounce the save to avoid performance issues during pan/zoom
      saveTimeoutRef.current = setTimeout(() => {
        localStorage.setItem(`canvas_${user?.email || 'default'}`, JSON.stringify({
          elements,
          scale,
          stagePos,
          timestamp: new Date().toISOString()
        }));
      }, 500);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [elements, user, scale, stagePos]);

  const addToHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(newElements);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
    setElements(newElements);
    setHasUnsavedChanges(true);
  }, [history, historyStep]);

  const selectedElement = useMemo(() => 
    selectedId ? elements.find(el => el.id === selectedId) : null
  , [selectedId, elements]);
  
  const isTextContext = activeTool === 'text' || (selectedElement && selectedElement.type === 'text');
  
  // Memoize whether transformer should show
  const showTransformer = useMemo(() => 
    selectedId && selectedElement && ['rectangle', 'circle', 'triangle', 'mindmap'].includes(selectedElement.type)
  , [selectedId, selectedElement]);
  
  // Stable callback for selecting elements
  const handleSelectElement = useCallback((id) => {
    setSelectedId(id);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + Z - Undo
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (historyStep > 0) {
          const newStep = historyStep - 1;
          setHistoryStep(newStep);
          setElements(history[newStep]);
          toast.success('Undo');
        }
        return;
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y - Redo
      if ((cmdOrCtrl && e.shiftKey && e.key === 'z') || (cmdOrCtrl && e.key === 'y')) {
        e.preventDefault();
        if (historyStep < history.length - 1) {
          const newStep = historyStep + 1;
          setHistoryStep(newStep);
          setElements(history[newStep]);
          toast.success('Redo');
        }
        return;
      }

      // Ctrl/Cmd + S - Save to cloud
      if (cmdOrCtrl && e.key === 's') {
        e.preventDefault();
        handleCloudSave();
        return;
      }

      // Ctrl/Cmd + O - Open/Load canvas
      if (cmdOrCtrl && e.key === 'o') {
        e.preventDefault();
        handleLoadCanvases();
        return;
      }

      // Ctrl/Cmd + N - New canvas
      if (cmdOrCtrl && e.key === 'n') {
        e.preventDefault();
        handleNewCanvas();
        return;
      }

      // Ctrl/Cmd + E - Export
      if (cmdOrCtrl && e.key === 'e') {
        e.preventDefault();
        handleExport();
        return;
      }

      // Ctrl/Cmd + A - Select all (future feature, for now just prevent default)
      if (cmdOrCtrl && e.key === 'a') {
        e.preventDefault();
        toast.info('Select all - coming soon!');
        return;
      }

      // Ctrl/Cmd + D - Duplicate selected element
      if (cmdOrCtrl && e.key === 'd' && selectedId) {
        e.preventDefault();
        const selectedElement = elements.find(el => el.id === selectedId);
        if (selectedElement) {
          const duplicated = {
            ...selectedElement,
            id: `${selectedElement.type}_${Date.now()}`,
            x: selectedElement.x + 20,
            y: selectedElement.y + 20,
          };
          addToHistory([...elements, duplicated]);
          setSelectedId(duplicated.id);
          toast.success('Element duplicated');
        }
        return;
      }

      // Delete or Backspace - Delete selected element
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        handleDeleteElement(selectedId);
        return;
      }

      // Escape - Deselect and cancel current tool
      if (e.key === 'Escape') {
        setSelectedId(null);
        setConnectorStart(null);
        setConnectorPreview(null);
        if (activeTool !== 'select') {
          setActiveTool('select');
          toast.info('Tool: Select');
        }
        return;
      }

      // Tab - Create child mindmap node
      if (e.key === 'Tab' && selectedId) {
        e.preventDefault();
        const selectedElement = elements.find(el => el.id === selectedId);
        if (selectedElement && selectedElement.type === 'mindmap') {
          createChildMindmapNode(selectedElement);
        }
        return;
      }

      // Number keys for quick tool selection
      if (e.key === '1') {
        setActiveTool('select');
        toast.info('Tool: Select');
        return;
      }
      if (e.key === '2') {
        setActiveTool('rectangle');
        toast.info('Tool: Rectangle');
        return;
      }
      if (e.key === '3') {
        setActiveTool('circle');
        toast.info('Tool: Circle');
        return;
      }
      if (e.key === '4') {
        setActiveTool('triangle');
        toast.info('Tool: Triangle');
        return;
      }
      if (e.key === '5') {
        setActiveTool('connector');
        toast.info('Tool: Connector');
        return;
      }
      if (e.key === '6') {
        setActiveTool('pencil');
        toast.info('Tool: Pencil');
        return;
      }
      if (e.key === '7') {
        setActiveTool('text');
        toast.info('Tool: Text');
        return;
      }
      if (e.key === '8') {
        setActiveTool('sticky');
        toast.info('Tool: Sticky Note');
        return;
      }
      if (e.key === '9') {
        setActiveTool('eraser');
        toast.info('Tool: Eraser');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, elements, historyStep, history, activeTool, connectorStart]);

  const handleUndo = useCallback(() => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      setElements(history[historyStep - 1]);
    }
  }, [history, historyStep]);

  const handleRedo = useCallback(() => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
      setElements(history[historyStep + 1]);
    }
  }, [history, historyStep]);

  const handleToolChange = (tool) => {
    setActiveTool(tool);
    setSelectedId(null);
  };

  // Update color pickers when a NEW element is selected (not on every element change)
  useEffect(() => {
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement) {
        if (selectedElement.fill) setSelectedColor(selectedElement.fill);
        if (selectedElement.stroke) setSelectedStroke(selectedElement.stroke);
        if (selectedElement.strokeWidth) setStrokeWidth(selectedElement.strokeWidth);
        if (selectedElement.type === 'text') {
          if (selectedElement.fontSize) setTextFontSize(selectedElement.fontSize);
          setIsTextBold(!!selectedElement.isBold);
          setIsTextItalic(!!selectedElement.isItalic);
          setIsTextStrikethrough(!!selectedElement.isStrikethrough);
        }
      }
    }
  }, [selectedId]); // Only run when selection changes, not on every element update

  // Update selected element when colors change
  const handleColorChange = (newColor) => {
    setSelectedColor(newColor);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement) {
        // Update fill color for elements that support it
        if (['rectangle', 'circle', 'triangle', 'mindmap'].includes(selectedElement.type)) {
          handleElementChange(selectedId, { fill: newColor });
        } else if (selectedElement.type === 'sticky') {
          handleElementChange(selectedId, { color: newColor });
        } else if (selectedElement.type === 'text') {
          handleElementChange(selectedId, { fill: newColor });
        } else if (selectedElement.type === 'drawing') {
          handleElementChange(selectedId, { stroke: newColor });
        }
      }
    }
  };

  const handleTextFontSizeChange = (newSize) => {
    setTextFontSize(newSize);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement && selectedElement.type === 'text') {
        handleElementChange(selectedId, { fontSize: newSize });
      }
    }
  };

  const handleToggleTextBold = () => {
    const next = !isTextBold;
    setIsTextBold(next);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement && selectedElement.type === 'text') {
        handleElementChange(selectedId, { isBold: next });
      }
    }
  };

  const handleToggleTextItalic = () => {
    const next = !isTextItalic;
    setIsTextItalic(next);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement && selectedElement.type === 'text') {
        handleElementChange(selectedId, { isItalic: next });
      }
    }
  };

  const handleToggleTextStrikethrough = () => {
    const next = !isTextStrikethrough;
    setIsTextStrikethrough(next);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement && selectedElement.type === 'text') {
        handleElementChange(selectedId, { isStrikethrough: next });
      }
    }
  };

  const handleStrokeChange = (newStroke) => {
    setSelectedStroke(newStroke);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement) {
        // Update stroke color for elements that support it
        if (['rectangle', 'circle', 'triangle', 'mindmap', 'drawing', 'connector'].includes(selectedElement.type)) {
          handleElementChange(selectedId, { stroke: newStroke });
        }
      }
    }
  };

  const handleStrokeWidthChange = (newWidth) => {
    setStrokeWidth(newWidth);
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement && (selectedElement.strokeWidth !== undefined)) {
        handleElementChange(selectedId, { strokeWidth: newWidth });
      }
    }
  };

  const getRelativePointerPosition = (stage) => {
    const pointerPosition = stage.getPointerPosition();
    const stageAttrs = stage.attrs;
    
    // Convert screen coordinates to canvas coordinates
    return {
      x: (pointerPosition.x - stageAttrs.x) / stageAttrs.scaleX,
      y: (pointerPosition.y - stageAttrs.y) / stageAttrs.scaleY,
    };
  };

  // Find nearby shape for connector anchoring
  const findNearbyShape = (point, elementsList) => {
    const SNAP_DISTANCE = 30; // pixels
    
    for (const element of elementsList) {
      if (element.type === 'rectangle' || element.type === 'circle' || element.type === 'triangle' || element.type === 'mindmap') {
        const bounds = getShapeBounds(element);
        
        // Check if point is near any edge of the shape
        const distances = {
          top: Math.abs(point.y - bounds.top),
          bottom: Math.abs(point.y - bounds.bottom),
          left: Math.abs(point.x - bounds.left),
          right: Math.abs(point.x - bounds.right)
        };
        
        // Find closest edge
        const minDistance = Math.min(...Object.values(distances));
        
        if (minDistance < SNAP_DISTANCE) {
          // Check if point is within horizontal/vertical bounds
          const withinHorizontal = point.x >= bounds.left - SNAP_DISTANCE && point.x <= bounds.right + SNAP_DISTANCE;
          const withinVertical = point.y >= bounds.top - SNAP_DISTANCE && point.y <= bounds.bottom + SNAP_DISTANCE;
          
          let side = null;
          if (distances.top === minDistance && withinHorizontal) side = 'top';
          else if (distances.bottom === minDistance && withinHorizontal) side = 'bottom';
          else if (distances.left === minDistance && withinVertical) side = 'left';
          else if (distances.right === minDistance && withinVertical) side = 'right';
          
          if (side) {
            return {
              shapeId: element.id,
              side: side
            };
          }
        }
      }
    }
    
    return null;
  };

  // Get shape bounds for anchor calculations
  const getShapeBounds = (shape) => {
    if (shape.type === 'rectangle' || shape.type === 'mindmap') {
      return {
        left: shape.x,
        right: shape.x + (shape.width || 100),
        top: shape.y,
        bottom: shape.y + (shape.height || 100)
      };
    } else if (shape.type === 'circle') {
      const radius = (shape.radius || 50);
      return {
        left: shape.x - radius,
        right: shape.x + radius,
        top: shape.y - radius,
        bottom: shape.y + radius
      };
    } else if (shape.type === 'triangle') {
      const w = shape.width || 120;
      const h = shape.height || 100;
      return {
        left: shape.x - w / 2,
        right: shape.x + w / 2,
        top: shape.y - h / 2,
        bottom: shape.y + h / 2
      };
    }
    return { left: 0, right: 0, top: 0, bottom: 0 };
  };

  // Calculate anchor point position based on shape and side
  const getAnchorPoint = (shape, side) => {
    const bounds = getShapeBounds(shape);
    const centerX = (bounds.left + bounds.right) / 2;
    const centerY = (bounds.top + bounds.bottom) / 2;
    
    switch (side) {
      case 'top': return { x: centerX, y: bounds.top };
      case 'bottom': return { x: centerX, y: bounds.bottom };
      case 'left': return { x: bounds.left, y: centerY };
      case 'right': return { x: bounds.right, y: centerY };
      default: return { x: centerX, y: centerY };
    }
  };

  const handleStageClick = (e) => {
    // Handle eraser tool - delete clicked element
    if (activeTool === 'eraser' && e.target !== e.target.getStage()) {
      // Try to find the element ID from the clicked target or its parents
      let clickedId = e.target.name() || e.target.attrs?.id;
      
      // Check parent groups (for connectors)
      if (!clickedId && e.target.parent) {
        clickedId = e.target.parent.attrs?.id || e.target.parent.name();
      }
      
      // Check grandparent (for nested groups)
      if (!clickedId && e.target.parent?.parent) {
        clickedId = e.target.parent.parent.attrs?.id || e.target.parent.parent.name();
      }
      
      if (clickedId) {
        handleDeleteElement(clickedId);
      }
      return;
    }

    // Handle connector tool (check before stage click detection)
    if (activeTool === 'connector') {
      const stage = e.target.getStage();
      const pointerPosition = getRelativePointerPosition(stage);
      
      if (!connectorStart) {
        // First click - set start point and create preview
        const startAnchor = findNearbyShape(pointerPosition, elements);
        setConnectorStart({ ...pointerPosition, anchor: startAnchor });
        setConnectorPreview({
          start: pointerPosition,
          end: pointerPosition
        });
      } else {
        // Second click - create connector
        const endAnchor = findNearbyShape(pointerPosition, elements);
        const newConnector = {
          id: `connector_${Date.now()}`,
          type: 'connector',
          points: [connectorStart.x, connectorStart.y, pointerPosition.x, pointerPosition.y],
          stroke: selectedStroke,
          strokeWidth,
          fill: selectedStroke,
          startAnchor: connectorStart.anchor,
          endAnchor: endAnchor
        };
        addToHistory([...elements, newConnector]);
        setConnectorStart(null);
        setConnectorPreview(null);
      }
      return;
    }

    // Deselect when clicking on empty area
    if (e.target === e.target.getStage()) {
      setSelectedId(null);
      
      // Reset connector if switching tools
      setConnectorStart(null);
      setConnectorPreview(null);
      
      // Add new element based on active tool
      if (activeTool !== 'select' && activeTool !== 'pan' && activeTool !== 'pencil' && activeTool !== 'eraser') {
        const stage = e.target.getStage();
        const pointerPosition = getRelativePointerPosition(stage);
        const newElement = createNewElement(activeTool, pointerPosition);
        
        if (newElement) {
          addToHistory([...elements, newElement]);
          // Reset to select tool after creating element
          setActiveTool('select');
          setSelectedId(newElement.id);
        }
      }
    }
  };

  const createNewElement = (tool, position) => {
    const id = `${tool}_${Date.now()}`;
    
    switch (tool) {
      case 'sticky':
        return {
          id,
          type: 'sticky',
          x: position.x - 100, // Center on click
          y: position.y - 100,
          text: '',
          color: STICKY_COLORS[Math.floor(Math.random() * STICKY_COLORS.length)],
          width: 200,
          height: 200
        };
      
      case 'text':
        return {
          id,
          type: 'text',
          x: position.x,
          y: position.y,
          text: '',
          fontSize: textFontSize,
          isBold: isTextBold,
          isItalic: isTextItalic,
          isStrikethrough: isTextStrikethrough
        };
      
      case 'rectangle':
        return {
          id,
          type: 'rectangle',
          x: position.x - 75, // Center on click
          y: position.y - 50,
          width: 150,
          height: 100,
          fill: selectedColor,
          stroke: selectedStroke,
          strokeWidth
        };
      
      case 'circle':
        return {
          id,
          type: 'circle',
          x: position.x, // Circle is already centered
          y: position.y,
          radius: 60,
          fill: selectedColor,
          stroke: selectedStroke,
          strokeWidth
        };
      
      case 'triangle':
        return {
          id,
          type: 'triangle',
          x: position.x, // Triangle center point
          y: position.y,
          width: 120,
          height: 100,
          fill: selectedColor,
          stroke: selectedStroke,
          strokeWidth
        };
      
      case 'mindmap':
        return {
          id,
          type: 'mindmap',
          x: position.x - 75, // Center on click
          y: position.y - 25,
          width: 150,
          height: 50,
          fill: selectedColor,
          stroke: selectedStroke,
          strokeWidth,
          text: ''
        };
      
      default:
        return null;
    }
  };

  const handleMouseDown = (e) => {
    if (activeTool === 'pencil') {
      setIsDrawing(true);
      const stage = e.target.getStage();
      const pos = getRelativePointerPosition(stage);
      const newLine = {
        id: `line_${Date.now()}`,
        type: 'line',
        points: [pos.x, pos.y],
        stroke: selectedStroke,
        strokeWidth
      };
      setCurrentLine(newLine);
    }
  };

  const handleMouseMove = (e) => {
    const stage = e.target.getStage();
    const pos = getRelativePointerPosition(stage);

    // Update connector preview (don't return, just update)
    if (activeTool === 'connector' && connectorPreview) {
      setConnectorPreview({
        ...connectorPreview,
        end: pos
      });
    }

    // Handle pencil drawing
    if (isDrawing && activeTool === 'pencil' && currentLine) {
      const newPoints = currentLine.points.concat([pos.x, pos.y]);
      setCurrentLine({ ...currentLine, points: newPoints });
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentLine) {
      addToHistory([...elements, currentLine]);
      setCurrentLine(null);
    }
    setIsDrawing(false);
  };

  const handleElementChange = useCallback((id, changes) => {
    setElements(prev => {
      const newElements = prev.map(el => 
        el.id === id ? { ...el, ...changes } : el
      );
      // Update history
      setHistory(h => {
        const newHistory = h.slice(0, historyStep + 1);
        newHistory.push(newElements);
        return newHistory;
      });
      setHistoryStep(s => s + 1);
      setHasUnsavedChanges(true);
      return newElements;
    });
  }, [historyStep]);

  const handleDeleteElement = (elementId) => {
    // Remove the element
    let newElements = elements.filter(el => el.id !== elementId);
    
    // Also remove any connectors that are connected to this element
    newElements = newElements.filter(el => {
      if (el.type === 'connector') {
        // Remove connector if it's connected to the deleted element
        const connectedToDeleted = 
          el.startAnchor?.shapeId === elementId || 
          el.endAnchor?.shapeId === elementId;
        return !connectedToDeleted;
      }
      return true;
    });
    
    addToHistory(newElements);
    setSelectedId(null);
    toast.success('Element deleted');
  };

  const createChildMindmapNode = (parentNode) => {
    // Get existing children of this parent
    const existingSiblings = getChildNodes(parentNode.id, elements);
    
    // Find optimal position for new child (avoids overlapping with siblings)
    const newNodeDimensions = { width: 150, height: 50 };
    const optimalPosition = findOptimalChildPosition(parentNode, existingSiblings, newNodeDimensions);
    
    // Create new child node at the optimal position
    const childNode = {
      id: `mindmap_${Date.now()}`,
      type: 'mindmap',
      x: optimalPosition.x,
      y: optimalPosition.y,
      width: newNodeDimensions.width,
      height: newNodeDimensions.height,
      fill: parentNode.fill,
      stroke: parentNode.stroke,
      strokeWidth: parentNode.strokeWidth,
      text: ''
    };

    // Calculate connector anchors based on actual positions
    const anchors = calculateConnectorAnchors(parentNode, childNode);

    // Create connector from parent to child
    const connector = {
      id: `connector_${Date.now() + 1}`,
      type: 'connector',
      points: anchors.points,
      stroke: parentNode.stroke,
      strokeWidth: 2,
      fill: parentNode.stroke,
      startAnchor: {
        shapeId: parentNode.id,
        side: anchors.startAnchor.side
      },
      endAnchor: {
        shapeId: childNode.id,
        side: anchors.endAnchor.side
      }
    };

    // Add both to canvas (no re-layout of existing nodes)
    const newElements = [...elements, childNode, connector];
    
    addToHistory(newElements);
    
    // Select the new child node
    setSelectedId(childNode.id);
    
    // Automatically open text editor for the new node
    setTimeout(() => {
      handleShapeTextEditWithElements(childNode, newElements);
    }, 150);

    toast.success('Child node created - Press Enter to confirm');
  };

  const handleShapeTextEditWithElements = (shape, elementsArray) => {
    const stage = stageRef.current;
    if (!stage) return;

    // Create textarea for editing
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    // Get shape position on screen
    const shapePos = {
      x: shape.x,
      y: shape.y
    };

    // Convert canvas coordinates to screen coordinates
    const scale = stage.scaleX();
    const stagePos = stage.position();
    const containerRect = stage.container().getBoundingClientRect();

    const screenX = shapePos.x * scale + stagePos.x + containerRect.left;
    const screenY = shapePos.y * scale + stagePos.y + containerRect.top;

    // Calculate dimensions based on shape type
    let textWidth, textHeight, textX, textY;
    if (shape.type === 'rectangle' || shape.type === 'mindmap') {
      textWidth = (shape.width || 100) * scale;
      textHeight = (shape.height || 100) * scale;
      textX = screenX;
      textY = screenY;
    } else if (shape.type === 'circle') {
      const radius = (shape.radius || 50) * scale;
      textWidth = radius * 2;
      textHeight = 40;
      textX = screenX - radius;
      textY = screenY - 20;
    } else if (shape.type === 'triangle') {
      textWidth = (shape.width || 120) * scale;
      textHeight = 40;
      textX = screenX - textWidth / 2;
      textY = screenY - 20;
    }

    // Style textarea
    textarea.value = shape.text || '';
    textarea.style.position = 'absolute';
    textarea.style.top = textY + 'px';
    textarea.style.left = textX + 'px';
    textarea.style.width = textWidth + 'px';
    textarea.style.height = textHeight + 'px';
    textarea.style.fontSize = Math.max(12, 16 * scale) + 'px';
    textarea.style.fontFamily = 'Arial';
    textarea.style.textAlign = 'center';
    textarea.style.border = '2px solid #3d9168';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.padding = '5px';
    textarea.style.background = 'rgba(255, 255, 255, 0.9)';
    textarea.style.zIndex = '10000';
    textarea.style.borderRadius = '4px';

    textarea.focus();
    textarea.select();

    let isSaving = false;

    const removeTextarea = () => {
      if (textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
    };

    const saveText = () => {
      if (isSaving) return;
      isSaving = true;
      
      // Update the element in the provided elements array
      const updatedElements = elementsArray.map(el => 
        el.id === shape.id ? { ...el, text: textarea.value } : el
      );
      addToHistory(updatedElements);
      removeTextarea();
    };

    const handleBlur = () => {
      saveText();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        textarea.removeEventListener('blur', handleBlur);
        saveText();
      } else if (e.key === 'Escape') {
        textarea.removeEventListener('blur', handleBlur);
        removeTextarea();
      }
      e.stopPropagation();
    };

    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', handleKeyDown);
  };

  const handleShapeTextEdit = useCallback((shapeId) => {
    const shape = elements.find(el => el.id === shapeId);
    if (!shape) return;
    
    const stage = stageRef.current;
    if (!stage) return;

    // Create textarea for editing
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    // Get shape position on screen
    const shapePos = {
      x: shape.x,
      y: shape.y
    };

    // Convert canvas coordinates to screen coordinates
    const currentScale = stage.scaleX();
    const currentStagePos = stage.position();
    const containerRect = stage.container().getBoundingClientRect();

    const screenX = shapePos.x * currentScale + currentStagePos.x + containerRect.left;
    const screenY = shapePos.y * currentScale + currentStagePos.y + containerRect.top;

    // Calculate dimensions based on shape type
    let textWidth, textHeight, textX, textY;
    if (shape.type === 'rectangle' || shape.type === 'mindmap') {
      textWidth = (shape.width || 100) * currentScale;
      textHeight = (shape.height || 100) * currentScale;
      textX = screenX;
      textY = screenY;
    } else if (shape.type === 'circle') {
      const radius = (shape.radius || 50) * currentScale;
      textWidth = radius * 2;
      textHeight = 40;
      textX = screenX - radius;
      textY = screenY - 20;
    } else if (shape.type === 'triangle') {
      textWidth = (shape.width || 120) * currentScale;
      textHeight = 40;
      textX = screenX - textWidth / 2;
      textY = screenY - 20;
    }

    // Style textarea
    textarea.value = shape.text || '';
    textarea.style.position = 'absolute';
    textarea.style.top = textY + 'px';
    textarea.style.left = textX + 'px';
    textarea.style.width = textWidth + 'px';
    textarea.style.height = textHeight + 'px';
    textarea.style.fontSize = Math.max(12, 16 * currentScale) + 'px';
    textarea.style.fontFamily = 'Arial';
    textarea.style.textAlign = 'center';
    textarea.style.border = '2px solid #3d9168';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.padding = '5px';
    textarea.style.background = 'rgba(255, 255, 255, 0.9)';
    textarea.style.zIndex = '10000';
    textarea.style.borderRadius = '4px';

    textarea.focus();
    textarea.select();

    let isSaving = false; // Flag to prevent double-save

    const removeTextarea = () => {
      if (textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
    };

    const saveText = () => {
      if (isSaving) return; // Prevent double execution
      isSaving = true;
      
      handleElementChange(shapeId, { text: textarea.value });
      removeTextarea();
    };

    const handleBlur = () => {
      saveText();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter saves and closes (Shift+Enter for new line)
        e.preventDefault();
        textarea.removeEventListener('blur', handleBlur); // Remove blur listener to prevent double-save
        saveText();
      } else if (e.key === 'Escape') {
        textarea.removeEventListener('blur', handleBlur); // Remove blur listener
        removeTextarea();
      }
      e.stopPropagation(); // Prevent canvas shortcuts
    };

    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', handleKeyDown);
  }, [elements, handleElementChange]);

  const handleElementDragEnd = useCallback((id, position) => {
    setElements(prev => {
      let newElements = prev.map(el => 
        el.id === id ? { ...el, ...position } : el
      );
      // Update anchored connectors
      newElements = updateAnchoredConnectors(newElements, id);
      // Update history
      setHistory(h => {
        const newHistory = h.slice(0, historyStep + 1);
        newHistory.push(newElements);
        return newHistory;
      });
      setHistoryStep(s => s + 1);
      setHasUnsavedChanges(true);
      return newElements;
    });
  }, [historyStep]);

  const handleElementTransform = useCallback((id, changes) => {
    setElements(prev => {
      let newElements = prev.map(el => 
        el.id === id ? { ...el, ...changes } : el
      );
      // Update anchored connectors
      newElements = updateAnchoredConnectors(newElements, id);
      // Update history
      setHistory(h => {
        const newHistory = h.slice(0, historyStep + 1);
        newHistory.push(newElements);
        return newHistory;
      });
      setHistoryStep(s => s + 1);
      setHasUnsavedChanges(true);
      return newElements;
    });
  }, [historyStep]);

  // Update connector positions when anchored shapes move or resize
  const updateAnchoredConnectors = (elementsList, movedShapeId) => {
    return elementsList.map(el => {
      if (el.type === 'connector' && (el.startAnchor || el.endAnchor)) {
        // Check if this connector is connected to the moved shape
        const isConnectedToMoved = 
          el.startAnchor?.shapeId === movedShapeId || 
          el.endAnchor?.shapeId === movedShapeId;
        
        if (!isConnectedToMoved) return el;
        
        // Get both connected shapes
        const startShape = el.startAnchor?.shapeId 
          ? elementsList.find(s => s.id === el.startAnchor.shapeId)
          : null;
        const endShape = el.endAnchor?.shapeId
          ? elementsList.find(s => s.id === el.endAnchor.shapeId)
          : null;
        
        // If both shapes exist and are mindmap nodes, recalculate optimal anchors
        if (startShape && endShape && startShape.type === 'mindmap' && endShape.type === 'mindmap') {
          const anchors = calculateConnectorAnchors(startShape, endShape);
          return {
            ...el,
            points: anchors.points,
            startAnchor: anchors.startAnchor,
            endAnchor: anchors.endAnchor
          };
        }
        
        // Otherwise, just update the anchor points using existing sides
        const points = [...el.points];
        
        if (el.startAnchor && el.startAnchor.shapeId === movedShapeId && startShape) {
          const anchorPoint = getAnchorPoint(startShape, el.startAnchor.side);
          points[0] = anchorPoint.x;
          points[1] = anchorPoint.y;
        }
        
        if (el.endAnchor && el.endAnchor.shapeId === movedShapeId && endShape) {
          const anchorPoint = getAnchorPoint(endShape, el.endAnchor.side);
          points[2] = anchorPoint.x;
          points[3] = anchorPoint.y;
        }
        
        return { ...el, points };
      }
      return el;
    });
  };

  const handleZoomIn = () => {
    setScale(Math.min(scale * 1.2, 3));
  };

  const handleZoomOut = () => {
    setScale(Math.max(scale / 1.2, 0.3));
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the entire canvas? This will create a new canvas.')) {
      handleNewCanvas();
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify({
      elements,
      scale,
      stagePos,
      version: '1.0',
      timestamp: new Date().toISOString()
    }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `canvas_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target.result);
            if (data.elements) {
              addToHistory(data.elements);
            }
          } catch (error) {
            toast.error('Error importing canvas: Invalid file format');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleFileSave = async () => {
    if (canvasName === 'Untitled Canvas' || !canvasName.trim()) {
      toast.error('Please give your canvas a name before saving');
      setIsEditingName(true);
      return;
    }

    const dataStr = JSON.stringify({
      id: canvasId,
      name: canvasName,
      elements,
      scale,
      stagePos,
      version: '1.0',
      timestamp: new Date().toISOString()
    }, null, 2);

    // Check if File System Access API is supported
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${canvasName.replace(/[^a-z0-9]/gi, '_')}.json`,
          types: [{
            description: 'Canvas JSON File',
            accept: { 'application/json': ['.json'] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(dataStr);
        await writable.close();
        setHasUnsavedChanges(false);
        toast.success('Canvas saved to file');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('File save error:', error);
          toast.error('Failed to save file');
        }
      }
    } else {
      // Fallback to download
      handleExport();
    }
  };

  const handleFileLoad = async () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Load a file anyway?')) {
        return;
      }
    }

    // Check if File System Access API is supported
    if ('showOpenFilePicker' in window) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [{
            description: 'Canvas JSON File',
            accept: { 'application/json': ['.json'] }
          }]
        });
        const file = await handle.getFile();
        const contents = await file.text();
        const data = JSON.parse(contents);
        
        if (data.elements) {
          setElements(data.elements);
          setHistory([data.elements]);
          setHistoryStep(0);
          setCanvasName(data.name || 'Untitled Canvas');
          setCanvasId(data.id || null);
          if (data.scale) setScale(data.scale);
          if (data.stagePos) setStagePos(data.stagePos);
          setHasUnsavedChanges(false);
          setSelectedId(null);
          setHasAutoCentered(false);
          toast.success('Canvas loaded from file');
        } else {
          toast.error('Invalid canvas file format');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('File load error:', error);
          toast.error('Failed to load file');
        }
      }
    } else {
      // Fallback to file input
      handleImport();
    }
  };

  const handleLocalSave = () => {
    if (canvasName === 'Untitled Canvas' || !canvasName.trim()) {
      toast.error('Please give your canvas a name before saving');
      setIsEditingName(true);
      return;
    }

    try {
      // Get existing local canvases
      const localCanvasesStr = localStorage.getItem('omniapps_local_canvases') || '{}';
      const localCanvases = JSON.parse(localCanvasesStr);
      
      // Save current canvas with its name as key
      const saveKey = canvasId || `local_${Date.now()}`;
      localCanvases[saveKey] = {
        id: saveKey,
        name: canvasName,
        elements,
        scale,
        stagePos,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('omniapps_local_canvases', JSON.stringify(localCanvases));
      
      // Update canvasId if it was a new save
      if (!canvasId) {
        setCanvasId(saveKey);
      }
      
      setHasUnsavedChanges(false);
      toast.success('Canvas saved locally');
    } catch (error) {
      console.error('Local save error:', error);
      toast.error('Failed to save canvas locally');
    }
  };

  const handleCloudSave = async () => {
    const token = accountService.getAccessToken();
    if (!token) {
      toast.error('Please log in to save to cloud');
      return;
    }

    if (canvasName === 'Untitled Canvas' || !canvasName.trim()) {
      toast.error('Please give your canvas a name before saving to cloud');
      setIsEditingName(true);
      return;
    }

    setCloudSaveStatus('saving');
    try {
      await saveCanvasToCloud(token, { 
        id: canvasId, 
        elements, 
        scale,
        stagePos,
        name: canvasName 
      });
      setCloudSaveStatus('saved');
      setLastCloudSave(new Date());
      setHasUnsavedChanges(false);
      toast.success('Canvas saved to cloud');
      setTimeout(() => setCloudSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Cloud save error:', error);
      setCloudSaveStatus('error');
      toast.error('Failed to save canvas to cloud');
      setTimeout(() => setCloudSaveStatus('idle'), 3000);
    }
  };

  const handleLoadCanvases = async () => {
    // Toggle menu if already open
    if (showLoadMenu) {
      setShowLoadMenu(false);
      return;
    }

    // Load local canvases
    const localCanvasesStr = localStorage.getItem('omniapps_local_canvases') || '{}';
    const localCanvases = JSON.parse(localCanvasesStr);
    const localCanvasList = Object.values(localCanvases).map(canvas => ({
      dataKey: canvas.id,
      dataValue: canvas,
      isLocal: true
    }));

    // Try to load cloud canvases
    const token = accountService.getAccessToken();
    let cloudCanvasList = [];
    
    if (token) {
      try {
        const canvases = await listSavedCanvases(token);
        cloudCanvasList = canvases.map(c => ({ ...c, isLocal: false }));
      } catch (error) {
        console.error('Error loading cloud canvas list:', error);
      }
    }

    // Combine both lists (local first, then cloud)
    setSavedCanvases([...localCanvasList, ...cloudCanvasList]);
    setShowLoadMenu(true);
  };

  const handleDeleteCanvas = async (canvasData, event) => {
    event.stopPropagation(); // Prevent loading the canvas when clicking delete
    
    const canvasNameToDelete = canvasData.dataValue?.name || 'Untitled';
    if (!window.confirm(`Delete "${canvasNameToDelete}"? This cannot be undone.`)) {
      return;
    }

    const deleteCanvasId = canvasData.dataValue?.id || canvasData.dataKey;

    // Handle local canvas deletion
    if (canvasData.isLocal) {
      try {
        const localCanvasesStr = localStorage.getItem('omniapps_local_canvases') || '{}';
        const localCanvases = JSON.parse(localCanvasesStr);
        delete localCanvases[deleteCanvasId];
        localStorage.setItem('omniapps_local_canvases', JSON.stringify(localCanvases));
        
        // Refresh the list
        handleLoadCanvases();
        
        toast.success('Canvas deleted successfully');
      } catch (error) {
        console.error('Error deleting local canvas:', error);
        toast.error('Failed to delete canvas');
      }
      return;
    }

    // Handle cloud canvas deletion
    const token = accountService.getAccessToken();
    if (!token) {
      toast.error('Please log in to delete cloud canvases');
      return;
    }

    try {
      await deleteCanvasFromCloud(token, deleteCanvasId);
      
      // Refresh the list
      handleLoadCanvases();
      
      toast.success('Canvas deleted successfully');
    } catch (error) {
      console.error('Error deleting canvas:', error);
      toast.error('Failed to delete canvas');
    }
  };

  const handleLoadSpecificCanvas = async (canvasData) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Load this canvas anyway?')) {
        return;
      }
    }

    if (canvasData && canvasData.dataValue) {
      const data = canvasData.dataValue;
      setElements(data.elements || []);
      setHistory([data.elements || []]);
      setHistoryStep(0);
      setCanvasName(data.name || 'Untitled Canvas');
      setCanvasId(data.id || canvasData.dataKey);
      if (data.scale) setScale(data.scale);
      if (data.stagePos) setStagePos(data.stagePos);
      setLastCloudSave(new Date(data.timestamp));
      setHasUnsavedChanges(false);
      setShowLoadMenu(false);
      setSelectedId(null);
      setHasAutoCentered(false);
    }
  };

  const handleNewCanvas = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Create a new canvas anyway?')) {
        return;
      }
    }

    setElements([]);
    setHistory([[]]);
    setHistoryStep(0);
    setCanvasName('Untitled Canvas');
    setCanvasId(null);
    setLastCloudSave(null);
    setHasUnsavedChanges(false);
    setSelectedId(null);
    setCloudSaveStatus('idle');
    setHasAutoCentered(false);
  };

  // Auto-save to cloud every 30 seconds if there are changes and auto-save is enabled
  useEffect(() => {
    if (!cloudAutoSaveEnabled) return;
    
    const token = accountService.getAccessToken();
    if (!token || !isCloudSaveAvailable(token) || elements.length === 0 || canvasName === 'Untitled Canvas') {
      return;
    }

    const autoSaveInterval = setInterval(() => {
      handleCloudSave();
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [elements, canvasName, cloudAutoSaveEnabled]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (loadMenuRef.current && !loadMenuRef.current.contains(event.target)) {
        setShowLoadMenu(false);
      }
      // Close file menu if clicking outside
      if (showFileMenu && !event.target.closest('[data-file-menu]')) {
        setShowFileMenu(false);
      }
    };

    if (showLoadMenu || showFileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLoadMenu, showFileMenu]);

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const scaleBy = 1.05;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.3, Math.min(3, newScale));

    setScale(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
  };

  const getTouchDistanceAndCenter = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const center = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
    return { distance, center };
  };

  const handleTouchStart = (e) => {
    const touchEvent = e.evt;
    if (touchEvent.touches.length === 2) {
      const [t1, t2] = touchEvent.touches;
      const { distance, center } = getTouchDistanceAndCenter(t1, t2);
      pinchLastDistRef.current = distance;
      pinchLastCenterRef.current = center;
    }
  };

  const handleTouchMove = (e) => {
    const touchEvent = e.evt;
    if (touchEvent.touches.length === 2) {
      touchEvent.preventDefault();
      const [t1, t2] = touchEvent.touches;
      const { distance, center } = getTouchDistanceAndCenter(t1, t2);

      if (pinchLastDistRef.current && distance) {
        const stage = stageRef.current;
        if (!stage) return;

        const oldScale = scale;
        const scaleBy = distance / pinchLastDistRef.current;
        const newScaleRaw = oldScale * scaleBy;
        const newScale = Math.max(0.3, Math.min(3, newScaleRaw));

        const stageX = stagePos.x;
        const stageY = stagePos.y;

        const pointTo = {
          x: (center.x - stageX) / oldScale,
          y: (center.y - stageY) / oldScale,
        };

        const newStagePos = {
          x: center.x - pointTo.x * newScale,
          y: center.y - pointTo.y * newScale,
        };

        setScale(newScale);
        setStagePos(newStagePos);
      }

      pinchLastDistRef.current = distance;
      pinchLastCenterRef.current = center;
    }
  };

  const handleTouchEnd = (e) => {
    const touchEvent = e.evt;
    if (touchEvent.touches.length < 2) {
      pinchLastDistRef.current = null;
      pinchLastCenterRef.current = null;
    }
  };

  const handleCenterView = () => {
    if (!elements.length) {
      setStagePos({ x: 0, y: 0 });
      setScale(1);
      return;
    }

    // Compute content bounds
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    elements.forEach((el) => {
      if (el.type === 'rectangle' || el.type === 'mindmap' || el.type === 'sticky') {
        const w = el.width || 100;
        const h = el.height || 100;
        minX = Math.min(minX, el.x);
        minY = Math.min(minY, el.y);
        maxX = Math.max(maxX, el.x + w);
        maxY = Math.max(maxY, el.y + h);
      } else if (el.type === 'circle') {
        const r = el.radius || 50;
        minX = Math.min(minX, el.x - r);
        minY = Math.min(minY, el.y - r);
        maxX = Math.max(maxX, el.x + r);
        maxY = Math.max(maxY, el.y + r);
      } else if (el.type === 'triangle') {
        const w = el.width || 120;
        const h = el.height || 100;
        minX = Math.min(minX, el.x - w / 2);
        minY = Math.min(minY, el.y - h / 2);
        maxX = Math.max(maxX, el.x + w / 2);
        maxY = Math.max(maxY, el.y + h / 2);
      } else if (el.type === 'line' || el.type === 'connector') {
        if (Array.isArray(el.points) && el.points.length >= 2) {
          for (let i = 0; i < el.points.length; i += 2) {
            const px = el.points[i];
            const py = el.points[i + 1];
            minX = Math.min(minX, px);
            minY = Math.min(minY, py);
            maxX = Math.max(maxX, px);
            maxY = Math.max(maxY, py);
          }
        }
      } else if (el.type === 'text') {
        minX = Math.min(minX, el.x);
        minY = Math.min(minY, el.y);
        maxX = Math.max(maxX, el.x + 10);
        maxY = Math.max(maxY, el.y + 10);
      }
    });

    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
      return;
    }

    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;

    const stage = stageRef.current;
    if (!stage) return;

    const viewportWidth = stage.width();
    const viewportHeight = stage.height();

    const newX = viewportWidth / 2 - contentCenterX * scale;
    const newY = viewportHeight / 2 - contentCenterY * scale;

    setStagePos({ x: newX, y: newY });
  };

  // Auto-center once after elements are loaded (initial load or when loading a canvas)
  useEffect(() => {
    if (!hasAutoCentered && elements.length > 0 && stageRef.current) {
      handleCenterView();
      setHasAutoCentered(true);
    }
  }, [elements, hasAutoCentered]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#363636',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      {/* Header with logo and user menu */}
      <div className="bg-white border-b border-gray-200 px-2 sm:px-4 py-2 flex items-center justify-between gap-2">
        {/* Left: Logo and Canvas Name */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center text-white font-bold flex-shrink-0">
            OC
          </div>
          <span className="hidden sm:inline text-sm text-gray-500 flex-shrink-0">Omniapps Canvas</span>
          <span className="hidden sm:inline text-gray-300">|</span>
          
          {isEditingName ? (
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <input
                type="text"
                value={tempCanvasName}
                onChange={(e) => setTempCanvasName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCanvasName(tempCanvasName.trim() || 'Untitled Canvas');
                    setIsEditingName(false);
                  } else if (e.key === 'Escape') {
                    setIsEditingName(false);
                  }
                }}
                className="px-2 py-1 text-sm border border-primary-500 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-0 flex-1"
                placeholder="Canvas name"
                autoFocus
              />
              <button
                onClick={() => {
                  setCanvasName(tempCanvasName.trim() || 'Untitled Canvas');
                  setIsEditingName(false);
                }}
                className="p-1 text-green-600 hover:bg-green-50 rounded flex-shrink-0"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsEditingName(false)}
                className="p-1 text-red-600 hover:bg-red-50 rounded flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 group min-w-0 flex-1">
              <h1 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                {canvasName}
                {hasUnsavedChanges && <span className="text-orange-500 ml-1">*</span>}
              </h1>
              <button
                onClick={() => {
                  setTempCanvasName(canvasName);
                  setIsEditingName(true);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-opacity flex-shrink-0"
                title="Edit canvas name"
              >
                <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </button>
            </div>
          )}
        </div>
        
        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* New Canvas Button */}
          <button
            onClick={handleNewCanvas}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            title="New canvas"
          >
            <FilePlus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
          </button>

          {/* Load Button */}
          <div className="relative" ref={loadMenuRef}>
            <button
              onClick={handleLoadCanvases}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              title="Load canvas"
            >
              <FolderOpen className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
            </button>
            
            {showLoadMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                <div className="px-3 py-2 border-b border-gray-200">
                  <h3 className="font-semibold text-sm text-gray-900">Saved Canvases</h3>
                </div>
                {savedCanvases.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    No saved canvases found
                  </div>
                ) : (
                  savedCanvases.map((canvas, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 group"
                    >
                      <button
                        onClick={() => handleLoadSpecificCanvas(canvas)}
                        className="flex-1 text-left text-sm flex flex-col gap-1 min-w-0"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {canvas.dataValue?.name || 'Untitled'}
                          </span>
                          {canvas.isLocal ? (
                            <HardDrive className="h-3 w-3 text-gray-400 flex-shrink-0" title="Local" />
                          ) : (
                            <Cloud className="h-3 w-3 text-blue-400 flex-shrink-0" title="Cloud" />
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {canvas.dataValue?.timestamp ? new Date(canvas.dataValue.timestamp).toLocaleString() : ''}
                        </span>
                      </button>
                      <button
                        onClick={(e) => handleDeleteCanvas(canvas, e)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Delete canvas"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* File Menu */}
          <div className="relative" data-file-menu>
            <button
              onClick={() => setShowFileMenu(!showFileMenu)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
              title="File menu"
            >
              <Menu className="h-4 w-4" />
              <span className="text-xs sm:text-sm hidden sm:inline">File</span>
            </button>
            
            {showFileMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-3 py-2 border-b border-gray-200">
                  <h3 className="font-semibold text-sm text-gray-900">File Options</h3>
                </div>
                
                {/* Open Section */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">Open</p>
                  <button
                    onClick={() => { handleFileLoad(); setShowFileMenu(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    <FileUp className="h-4 w-4" />
                    Open from File...
                  </button>
                  <button
                    onClick={() => { handleLoadCanvases(); setShowFileMenu(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    <FolderOpen className="h-4 w-4" />
                    Open Saved Canvas...
                  </button>
                </div>
                
                {/* Save Section */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">Save</p>
                  <button
                    onClick={() => { handleFileSave(); setShowFileMenu(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    <FileDown className="h-4 w-4" />
                    Save to File...
                  </button>
                  <button
                    onClick={() => { handleLocalSave(); setShowFileMenu(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    <HardDrive className="h-4 w-4" />
                    Save to Browser
                  </button>
                  <button
                    onClick={() => { handleCloudSave(); setShowFileMenu(false); }}
                    disabled={cloudSaveStatus === 'saving'}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded disabled:opacity-50"
                  >
                    <Cloud className="h-4 w-4" />
                    Save to Cloud
                    {cloudSaveStatus === 'saving' && <span className="text-xs text-blue-500 ml-auto">Saving...</span>}
                  </button>
                </div>
                
                {/* New Canvas */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <button
                    onClick={() => { handleNewCanvas(); setShowFileMenu(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    <FilePlus className="h-4 w-4" />
                    New Canvas
                  </button>
                </div>
                
                {/* Settings Section */}
                <div className="px-3 py-2">
                  <p className="text-xs text-gray-500 mb-2">Settings</p>
                  <button
                    onClick={() => setCloudAutoSaveEnabled(!cloudAutoSaveEnabled)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                  >
                    {cloudAutoSaveEnabled ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-4 w-4 text-gray-400" />
                    )}
                    Cloud Auto-Save
                    <span className={`ml-auto text-xs ${cloudAutoSaveEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                      {cloudAutoSaveEnabled ? 'On' : 'Off'}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              title={user?.email}
            >
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              <span className="text-xs sm:text-sm text-gray-700 hidden md:inline">{user?.email}</span>
            </button>
          
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <button
                onClick={() => {
                  logout();
                  setShowUserMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      <CanvasToolbar
        activeTool={activeTool}
        onToolChange={handleToolChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onCenterView={handleCenterView}
        onClear={handleClear}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onExport={handleExport}
        onImport={handleImport}
        canUndo={historyStep > 0}
        canRedo={historyStep < history.length - 1}
        selectedColor={selectedColor}
        onColorChange={handleColorChange}
        selectedStroke={selectedStroke}
        onStrokeChange={handleStrokeChange}
        strokeWidth={strokeWidth}
        onStrokeWidthChange={handleStrokeWidthChange}
        textFontSize={textFontSize}
        onTextFontSizeChange={handleTextFontSizeChange}
        isTextBold={isTextBold}
        onToggleTextBold={handleToggleTextBold}
        isTextItalic={isTextItalic}
        onToggleTextItalic={handleToggleTextItalic}
        isTextStrikethrough={isTextStrikethrough}
        onToggleTextStrikethrough={handleToggleTextStrikethrough}
        isTextContext={isTextContext}
      />
      
      <div ref={containerRef} className="flex-1 overflow-hidden relative">
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          scaleX={scale}
          scaleY={scale}
          x={stagePos.x}
          y={stagePos.y}
          draggable={activeTool === 'pan'}
          onClick={handleStageClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <Layer>
            {/* Grid background */}
            {Array.from({ length: 100 }).map((_, i) => (
              <React.Fragment key={`grid-${i}`}>
                {/* Vertical lines */}
                <DrawingLine
                  id={`grid-v-${i}`}
                  points={[i * GRID_SIZE, -5000, i * GRID_SIZE, 5000]}
                  stroke="#e5e7eb"
                  strokeWidth={1}
                />
                {/* Horizontal lines */}
                <DrawingLine
                  id={`grid-h-${i}`}
                  points={[-5000, i * GRID_SIZE, 5000, i * GRID_SIZE]}
                  stroke="#e5e7eb"
                  strokeWidth={1}
                />
              </React.Fragment>
            ))}

            {/* Render all elements */}
            {elements.map((element) => {
              const isSelected = element.id === selectedId;
              
              if (element.type === 'sticky') {
                return (
                  <StickyNote
                    key={element.id}
                    {...element}
                    isSelected={isSelected}
                    onSelect={handleSelectElement}
                    onChange={handleElementChange}
                    onDragEnd={handleElementDragEnd}
                  />
                );
              }
              
              if (element.type === 'text') {
                return (
                  <TextElement
                    key={element.id}
                    {...element}
                    isSelected={isSelected}
                    onSelect={handleSelectElement}
                    onChange={handleElementChange}
                    onDragEnd={handleElementDragEnd}
                  />
                );
              }
              
              if (['rectangle', 'circle', 'triangle', 'mindmap'].includes(element.type)) {
                return (
                  <Shape
                    key={element.id}
                    {...element}
                    isSelected={isSelected}
                    onSelect={handleSelectElement}
                    onDragEnd={handleElementDragEnd}
                    onTransform={handleElementTransform}
                    onTextEdit={handleShapeTextEdit}
                  />
                );
              }
              
              if (element.type === 'connector') {
                return (
                  <Connector
                    key={element.id}
                    {...element}
                    isSelected={isSelected}
                    onSelect={handleSelectElement}
                    onChange={handleElementChange}
                  />
                );
              }
              
              if (element.type === 'line') {
                return (
                  <DrawingLine
                    key={element.id}
                    {...element}
                    isSelected={isSelected}
                    onSelect={handleSelectElement}
                  />
                );
              }
              
              return null;
            })}

            {/* Current drawing line */}
            {currentLine && (
              <DrawingLine {...currentLine} />
            )}

            {/* Connector preview */}
            {connectorPreview && (
              <Connector
                id="connector-preview"
                points={[
                  connectorPreview.start.x,
                  connectorPreview.start.y,
                  connectorPreview.end.x,
                  connectorPreview.end.y
                ]}
                stroke={selectedStroke}
                strokeWidth={strokeWidth}
                fill={selectedStroke}
                isSelected={false}
                onSelect={() => {}}
                onChange={() => {}}
              />
            )}

            {/* Transformer for resizing shapes */}
            {showTransformer && (
              <Transformer selectedShapeName={selectedId} />
            )}
          </Layer>
        </Stage>

        {/* Info overlay */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-4 py-2 text-sm text-gray-600">
          <div>Zoom: {Math.round(scale * 100)}%</div>
          <div>Elements: {elements.length}</div>
          <div>Tool: {activeTool}</div>
          {connectorStart && (
            <div className="text-primary-600 font-medium">Click to set end point</div>
          )}
        </div>
      </div>
    </div>
  );
};
